import axios from 'axios';
import { PaginationInfo, RequestParams } from 'types/pagination';
import { Course, CrmCouseDetails, Link, Teacher } from 'types/course';
import { Product } from 'types/product';
import { CourseMeeting } from 'types/specs';

export const getCourses = async (
    setCourses: (val: Course[]) => void,
    requestParams: RequestParams,
    setPaginationInfo?: (val: PaginationInfo) => void
) => {
    const params: any = {
        page: requestParams.page,
        per_page: requestParams.per_page,
        sort: `${requestParams.sort[0].sort === 'desc' ? '-' : ''}${requestParams.sort[0].field}`
    };
    requestParams.filters?.forEach((filter) => {
        params[`filter[${filter.name}]`] = filter.value;
    });
    try {
        const response = await axios.get('/courses', { params });
        const coursesData: Course[] = response.data.courses.data;

        setPaginationInfo?.({
            total: response.data.courses.total,
            last_page: response.data.courses.last_page,
            per_page: response.data.courses.per_page,
            sortable_fields: response.data.courses.sorts,
            available_filters: response.data.courses.filters
        });

        setCourses(coursesData);
    } catch (err) {
        console.error(`error in getting courses ${err}`);
    }
};

export const createCourse = async (course: Course, onSuccess: (val: boolean) => void) => {
    const formData = new FormData();

    const linksData: Link[] = [];
    course.links!.forEach((link: Link) => {
        const { photo } = link;
        if (photo) {
            if (photo.upload_data) {
                formData.append(
                    photo.file_path
                        .replace(/[^/.]+$/, '')
                        .replace(/\s+/g, '')
                        .replace(/(\.)/g, ''),
                    photo.upload_data
                );

                linksData.push({
                    ...link,
                    photo: {
                        ...photo,
                        file_path: photo.file_path
                            .replace(/[^/.]+$/, '')
                            .replace(/\s+/g, '')
                            .replace(/(\.)/g, '')
                    }
                });
            }
        }
    });

    formData.append('name', course.name);
    formData.append('product_id', course.product_id?.toString() || '');
    formData.append('teachers', JSON.stringify(course.teachers));
    formData.append('links', JSON.stringify(linksData));

    try {
        await axios.post('/courses', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
        onSuccess(true);
    } catch (err) {
        console.error(`error in create course ${err}`);
        onSuccess(false);
    }
};

export const getCourse = async (id: string, setCourse: (val: Course) => void, setMeetingDetails: (val: CourseMeeting[]) => void) => {
    try {
        const response = await axios.get(`/courses/${id}`);
        const course = response.data.course as Course;
        setCourse(course);
        if (course.meetings) {
            const updatedMeetings: any = course.meetings;
            if (updatedMeetings.length > 0) {
                for (let i = 0; i < updatedMeetings.length; i += 1) {
                    const meetingData = updatedMeetings[i];
                    // Check if meetingData.details exists, and create it if not
                    if (!meetingData.details) {
                        meetingData.details = {};
                    }
                    meetingData.details.start = meetingData.start;
                    meetingData.details.end = meetingData.end;
                    meetingData.details.meeting_date = meetingData.meeting_date;
                    meetingData.details.meeting_id = meetingData.meeting_id;
                    meetingData.details.product_id = course.product_id;
                }
                setMeetingDetails(updatedMeetings);
            }
        }
    } catch (err) {
        console.error(`error in getting course ${err}`);
    }
};

export const updateCourse = async (course: Course, onSuccess: (val: boolean) => void) => {
    const body = { ...course };
    try {
        if (!body.product_id && !body.product) {
            if (!body.information) {
                console.error('Information does not exist');
                onSuccess(false);
            } else {
                body.product_id = body.information?.product?.id;
                body.product = { name: body.information?.product?.name, id: body.information.product?.id };
                await axios.put(`/courses/${course.id}`, body);
                onSuccess(true);
            }
        } else {
            await axios.put(`/courses/${course.id}`, body);
            onSuccess(true);
        }
    } catch (err) {
        console.error(`error in update course ${err}`);
        onSuccess(false);
    }
};

export const disableCourse = async (id: string) => {
    try {
        await axios.delete(`/courses/${id}`);
    } catch (err) {
        console.error(`error in disable course ${err}`);
    }
};

export const getCrmCouses = async () => {
    const message = { message: 'Process is running...' };
    try {
        const crmCouses = await axios.get('/get-crm-courses');
        if (crmCouses.data === undefined) {
            return message;
        }
        return crmCouses.data;
    } catch (error) {
        console.error(`error while getting crm courses ${error}`);
        return message;
    }
};

export const getCrmCousesDetails = async (setDetails: (val: CrmCouseDetails[]) => void, setSuccessDetials: (val: boolean) => void) => {
    try {
        const response = await axios.get('get-crm-courses-details');
        const Details: CrmCouseDetails[] = response.data;
        setDetails(Details);
        setSuccessDetials(true);
    } catch (error) {
        console.error(`error in getting crm courses details ${error}`);
        setSuccessDetials(false);
    }
};

export const getProductList = async (setProductstList: (value: Product[]) => void) => {
    try {
        const response = await axios.get('/product/list');

        setProductstList(response.data.products);
    } catch (err) {
        console.error(`Error getting products list ${err}`);
    }
};
export const getTeacherList = async (setTeacherList: (value: Teacher[]) => void) => {
    try {
        const response = await axios.get('/users/list?by_role=Teacher');

        const TeacherData: Teacher[] = [];
        for (const [key, value] of Object.entries(response.data.users)) {
            TeacherData.push({ id: key, full_name: value as string });
        }

        setTeacherList(TeacherData);
    } catch (err) {
        console.error(`Error getting teacher list ${err}`);
    }
};
export const removeSpecification = async (course: Course, onSuccess: (val: boolean) => void) => {
    try {
        await axios.put(`courses/remove-specification/${course.id}`);
        onSuccess(false);
    } catch (err) {
        console.error(`error in update course ${err}`);
        onSuccess(false);
    }
};

export const getMettingBasedOnSpecs = async (spec_id: number, product_id: number, setMeetings: (val: CourseMeeting[]) => void) => {
    try {
        const response = await axios.get(`meeting-based-on-specs/${spec_id}`);
        const updatedMeetings = response.data.meeting;
        if (updatedMeetings.length > 0) {
            for (let i = 0; i < updatedMeetings.length; i += 1) {
                const meetingData = updatedMeetings[i];
                // Check if meetingData.details exists, and create it if not
                if (!meetingData.details) {
                    meetingData.details = {};
                }
                meetingData.details.start = meetingData.start;
                meetingData.details.end = meetingData.end;
                meetingData.details.meeting_date = meetingData.meeting_date;
                meetingData.details.meeting_id = meetingData.meeting_id;
                meetingData.details.product_id = product_id;
            }

            // Move setMeetings outside the loop
            setMeetings(updatedMeetings);
        } else {
            setMeetings(updatedMeetings);
        }
    } catch (error) {
        console.error(`error in Meeting Based on Product ${error}`);
    }
};

export const getAttendanceReport = async () => {
    const message = { message: 'Process is running...' };
    try {
        const report = await axios.get('/get-attendance-report');
        return report?.data;
    } catch (error) {
        console.error(`Error while getting attendance report: ${error}`);
        return error;
    }
};

export const AttendanceReport = async () => {
    try {
        const response = await axios.get('/attendance-report');
        if (response?.data?.status) {
            return response.data.data;
        }
        throw new Error(response?.data?.message || 'Failed to fetch the report');
    } catch (error) {
        console.error(`Error while getting attendance report: ${error}`);
        return error;
    }
};

export const QuestionnaireReport = async () => {
    try {
        const response = await axios.get('/onboarding-report');
        if (response?.data?.status) {
            return response.data.data;
        }
        throw new Error(response?.data?.message || 'Failed to fetch the report');
    } catch (error) {
        console.error(`Error while getting onboarding report: ${error}`);
        throw error;
    }
};

export default getCourses;
